import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import OfficeGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/office-group/office-group.entity";
import { ICreateOfficeGroup } from "./interfaces/create-office-group.interface";
import { IUpdateOfficeGroup } from "./interfaces/update-office-group.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_CONFIGURATION_URL;
const moduleName = "office-group";

@Injectable({
  providedIn: "root",
})
export class OfficeGroupService extends AbstractService<
  OfficeGroupAttributes,
  ICreateOfficeGroup,
  IUpdateOfficeGroup
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<OfficeGroupAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<OfficeGroupAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
      const headers: Record<string, any> = {};
      // see order-customer-export for more info about this header
      // ctx: stands for context
      if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
      return this.http.get<IPaginationResult<OfficeGroupAttributes>>(API_URL, { headers });
  }

  /**
   * Obtiene grupos de oficinas de venta con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - uno busca en 'name', otro en 'id'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: name, id
   * Array filters: statusId, companyId
   */
  findAllWithCount(
    options: IPagination<OfficeGroupAttributes>,
    dataToFilter: IFilters,
  ): Observable<IPaginationResult<OfficeGroupAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<OfficeGroupAttributes>[] => {
      const deletedFilter: FilterFn<OfficeGroupAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere('company.id' as keyof OfficeGroupAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere('statusId', Ops.in(...dataToFilter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por NAME (o solo condiciones si no hay búsqueda)
    const searchByNameQueryObject: FilterFn<OfficeGroupAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("name", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por ID (solo si hay búsqueda)
    const searchByIdQueryObject: FilterFn<OfficeGroupAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto, crea el OR)
      andWhere("id", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<OfficeGroupAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByNameQueryObject,
      ...searchByIdQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;

    return this.http.get<IPaginationResult<OfficeGroupAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<OfficeGroupAttributes[]>> {
    const query = buildQuery<OfficeGroupAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["id", "name"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<OfficeGroupAttributes[]>>(API_URL);
  }

  findByCompanyId(companyId: string): Observable<IRequestResult<OfficeGroupAttributes[]>> {
    const query = buildQuery<OfficeGroupAttributes>(
      where("company._id" as keyof OfficeGroupAttributes, Ops.eq(companyId)),
      andWhere("statusId", Ops.eq(StatusEnum.ACTIVE)),
      select(["id", "name"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<OfficeGroupAttributes[]>>(API_URL);
  }
}
